/**
 * Benchly Theme Configuration
 * Uses Fluent UI v9 theming system for consistent styling across the application
 */

import {
  BrandVariants,
  createLightTheme,
  createDarkTheme,
  Theme,
} from "@fluentui/react-components";

/**
 * Benchly brand color variants
 * Colors are defined from darkest (10) to lightest (160)
 * Primary brand color: #0D3C5A (navy blue)
 */
export const benchlyBrand: BrandVariants = {
  10: "#020305", // Darkest
  20: "#050A0F",
  30: "#081419",
  40: "#0A2F47", // Hover state
  50: "#0D3C5A", // Primary brand color
  60: "#0F4B73",
  70: "#115A8C",
  80: "#1369A5",
  90: "#1578BE",
  100: "#1787D7",
  110: "#3A96DC",
  120: "#5DA5E1",
  130: "#80B4E6",
  140: "#A3C3EB",
  150: "#C6D2F0",
  160: "#E9E1F5", // Lightest
};

/**
 * Create Benchly Light Theme
 * Based on Fluent UI web light theme with custom brand colors
 */
export const benchlyLightTheme: Theme = {
  ...createLightTheme(benchlyBrand),
  // Override specific tokens to match Benchly design
  fontFamilyBase: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  colorBrandForeground2: "#A0A0A0", // Even brighter for hover states
};

/**
 * Create Benchly Dark Theme
 * Based on Fluent UI web dark theme with custom brand colors
 * Optimized for readability with dark grey backgrounds and white text
 */
export const benchlyDarkTheme: Theme = {
  ...createDarkTheme(benchlyBrand),
  // Override specific tokens for better dark mode experience
  fontFamilyBase: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  
  // Dark mode backgrounds (dark grey instead of pure black)
  colorNeutralBackground1: "#2D2D2D", // Main page background
  colorNeutralBackground2: "#3A3A3A", // Section backgrounds
  colorNeutralBackground3: "#353535", // Card backgrounds
  
  // Dark mode text (whiter for better readability)
  colorNeutralForeground1: "#FFFFFF", // Primary text - pure white
  colorNeutralForeground2: "#E0E0E0", // Secondary text - almost white
  colorNeutralForeground3: "#C0C0C0", // Tertiary text - light grey
  
  // Brand colors for dark mode (brighter for visibility)
  colorBrandForeground1: "#5DA5E1", // Bright blue for brand text/numbers
  colorBrandForeground2: "#F0F0F0", // Even brighter for hover states

  
  // Dark mode borders
  colorNeutralStroke1: "#4A4A4A", // Default borders
  colorNeutralStroke2: "#555555", // Lighter borders
};

const THEME_STORAGE_KEY = "benchly_dark_mode";

/**
 * Detect if Office is running in dark mode.
 * Primary: reads Office.context.officeTheme (available in taskpane).
 * Fallback: reads localStorage (works in dialogs where officeTheme is unavailable).
 * @returns true if Office is in dark mode, false otherwise
 */
export const detectOfficeDarkMode = (): boolean => {
  try {
    if (typeof Office !== "undefined" && Office.context?.officeTheme) {
      const bgColor = Office.context.officeTheme.bodyBackgroundColor;
      if (bgColor) {
        // Convert hex to brightness value
        const hex = bgColor.replace("#", "");
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        const isDark = brightness < 128;
        // Persist for dialogs that can't access officeTheme
        try {
          localStorage.setItem(THEME_STORAGE_KEY, isDark ? "true" : "false");
        } catch {
          // Ignore storage errors
        }
        return isDark;
      }
    }

    // Fallback: read from localStorage (set by taskpane)
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  } catch (error) {
    console.warn("Failed to detect Office theme:", error);
    return false; // Default to light mode if detection fails
  }
};

/**
 * Typography constants (for reference)
 * Use Fluent UI typography tokens in components instead
 */
export const typography = {
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,
} as const;

/**
 * Word API Colors
 * These colors are used when directly manipulating Word document content
 * via the Word JavaScript API (not React components)
 */
export const wordApiColors = {
  hyperlinkBlue: "#0563C1", // Standard Office hyperlink blue
  textBlack: "#000000", // Default text color
  textPrimary: "#323130", // Primary text color (matches Fluent UI)
} as const;
